/**
 * IndexedDB wrapper for FlightSync offline queue.
 * Store: flights-queue (all parsed flights - synced and pending).
 * Max 500 flights (rolling).
 */
const DB_NAME = 'flightsync-db';
const DB_VERSION = 1;
const STORE = 'flights';
const MAX_ENTRIES = 500;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode = 'readonly') {
  const t = db.transaction(STORE, mode);
  return t.objectStore(STORE);
}

export async function addFlight(flight) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readwrite');
    const req = store.put(flight);
    req.onsuccess = () => resolve(flight);
    req.onerror = () => reject(req.error);
  });
}

export async function updateFlight(id, patch) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readwrite');
    const g = store.get(id);
    g.onsuccess = () => {
      const cur = g.result;
      if (!cur) return resolve(null);
      const next = { ...cur, ...patch };
      const p = store.put(next);
      p.onsuccess = () => resolve(next);
      p.onerror = () => reject(p.error);
    };
    g.onerror = () => reject(g.error);
  });
}

export async function listFlights() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readonly');
    const req = store.getAll();
    req.onsuccess = () => {
      const items = (req.result || []).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      resolve(items);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function listPending() {
  const items = await listFlights();
  return items.filter((f) => !f.synced);
}

export async function removeFlight(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readwrite');
    const req = store.delete(id);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

export async function pruneOldSynced() {
  const items = await listFlights();
  if (items.length <= MAX_ENTRIES) return 0;
  const toDelete = items
    .filter((f) => f.synced)
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))
    .slice(0, items.length - MAX_ENTRIES);
  for (const f of toDelete) await removeFlight(f.id);
  return toDelete.length;
}

export async function clearAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readwrite');
    const req = store.clear();
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}
